import { UnappliedPaymentSearchResultDto } from '../../shared/unapplied-payment/dto/unapplied-payment-search-result.dto';
import { DatagridService } from '../../../shared/widgets/datagrid/datagrid.service';
import { ConfirmationService, LazyLoadEvent, MenuItem } from 'primeng/api';
import { UnappliedPaymentDto } from '../../shared/unapplied-payment/dto/unapplied-payment.dto';
import { UnappliedPaymentSearchDto } from '../../shared/unapplied-payment/dto/unapplied-payment-search.dto';
import { UnappliedPaymentService } from '../../shared/unapplied-payment/unapplied-payment.service';
import { Component, OnInit, Input, OnChanges, SimpleChanges, Output, EventEmitter, ViewChild } from '@angular/core';
import { NotificationService, PagedResultGeneric, TranslationService } from 'src/app/shared';
import { AdjustUnappliedPaymentDialogComponent } from '../adjust-unapplied-payment-dialog/adjust-unapplied-payment-dialog.component';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { CreditCardService } from '../../shared/credit-card/credit-card.service';
import { CreditCardRefundRequest } from '../../shared/credit-card/credit-card-refund-request.model';
import { CreditCardRefundResponse } from '../../shared/credit-card/credit-card-refund-response.model';

@Component({
  selector: 'wl-unapplied-payments-grid',
  templateUrl: './unapplied-payments-grid.component.html',
  styleUrls: ['./unapplied-payments-grid.component.less'],
    standalone: false
})
export class UnappliedPaymentsGridComponent implements OnInit, OnChanges {
  @Input() profileId: number;
  @Output() loadedResults = new EventEmitter<UnappliedPaymentSearchResultDto>();
  @ViewChild(AdjustUnappliedPaymentDialogComponent) private _adjustmentDialog: AdjustUnappliedPaymentDialogComponent;
  @ViewChild('refundCD') private _refundConfirmDialog: ConfirmDialog;
  loading = false;
  searchParams: UnappliedPaymentSearchDto;
  searchResult: UnappliedPaymentSearchResultDto;
  rowMenuItems = new Map<number, MenuItem[]>();
  unappliedPaymentToDelete: UnappliedPaymentDto;

  constructor(
    private _unappliedPaymentService: UnappliedPaymentService,
    private _creditCardService: CreditCardService,
    private _datagridService: DatagridService,
    private _translationService: TranslationService,
    private _confirmationService: ConfirmationService,
    private _notificationService: NotificationService
  ) {
    this.searchParams = new UnappliedPaymentSearchDto();
    this.searchParams.PageSize = 10;
    this.searchResult = { Result: [] } as UnappliedPaymentSearchResultDto;
  }

  ngOnInit() {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes.profileId && this.profileId) {
      this.searchParams.ProfileId = this.profileId;
      this.search();
    }
  }

  public search() {
    this.loading = true;
    this._unappliedPaymentService.search(this.searchParams).then((result) => {
      this.searchParams.TotalCount = result.TotalCount;
      this.searchResult = result;
      this.loadedResults.emit(result);
      this.loading = false;
    });
  }

  lazyLoadUnappliedPayments(event: LazyLoadEvent) {
    this._datagridService.mapDatagridPagingToPagedSearch(event, this.searchParams);
    this.search();
  }

  actionItemsForUnappliedPayment(unappliedPayment: UnappliedPaymentDto): MenuItem[] {
    if (!this.rowMenuItems.has(unappliedPayment.UnappliedPaymentId)) {
      this.rowMenuItems.set(unappliedPayment.UnappliedPaymentId, [
        {
          label: this._translationService.getInstantTitlecase('common.adjust'),
          command: () => this.adjustUnappliedPayment(unappliedPayment),
        },
        {
          label: this._translationService.getInstantTitlecase('common.delete'),
          command: () => this.confirmDeletion(unappliedPayment),
        },
      ]);
    }
    return this.rowMenuItems.get(unappliedPayment.UnappliedPaymentId);
  }

  deleteWithoutRefunding() {
    this.processDeletion(this.unappliedPaymentToDelete);
    this._refundConfirmDialog.onReject();
  }

  private adjustUnappliedPayment(unappliedPayment: UnappliedPaymentDto) {
    this._adjustmentDialog.open(unappliedPayment);
  }

  private confirmDeletion(unappliedPayment: UnappliedPaymentDto) {
    this.unappliedPaymentToDelete = unappliedPayment;
    this._confirmationService.confirm({
      message: this._translationService.getInstant('revenue.transactions-grid.unapplied-payments.confirm-delete-message'),
      key: 'deleteUnappliedPaymentCD',
      accept: () => {
        this.promptRefundIfCreditPayment(unappliedPayment);
      },
      reject: () => {
        this.unappliedPaymentToDelete = null;
        this._confirmationService.close();
      },
    });
  }

  private processDeletion(unappliedPayment: UnappliedPaymentDto) {
    this.loading = true;
    this._unappliedPaymentService
      .delete(unappliedPayment.UnappliedPaymentId)
      .then(() => {
        this._notificationService.translateSuccess('revenue.transactions-grid.unapplied-payments.delete-success-message');
        this.search();
      })
      .catch((error) => {
        this._notificationService.httpError(error);
      })
      .finally(() => {
        this.loading = false;
        this.unappliedPaymentToDelete = null;
      });
  }

  private promptRefundIfCreditPayment(unappliedPayment: UnappliedPaymentDto): void {
    if (unappliedPayment.CC_CreditCardNum) {
      this._confirmationService.confirm({
        message: this._translationService.getInstant('revenue.transactions-grid.unapplied-payments.confirm-refund-message'),
        key: 'refundCreditCardCD',
        accept: () => {
          this.processRefund(unappliedPayment).then(() => {
            this.processDeletion(unappliedPayment);
          });
        },
        reject: () => {
          this._confirmationService.close();
        },
      });
    } else {
      this.processDeletion(unappliedPayment);
    }
  }

  private processRefund(unappliedPayment: UnappliedPaymentDto): Promise<CreditCardRefundResponse> {
    const refund = new CreditCardRefundRequest();
    refund.CreditCardTransactionId = +unappliedPayment.CC_CreditCardNum.replace('ccid_', '');
    refund.CreateAdjustments = false;
    refund.RefundAmount = -unappliedPayment.Amount;
    return this._creditCardService
      .refundCreditCardPayment(refund)
      .then((response) => {
        this._notificationService.translateSuccess('revenue.transactions-grid.unapplied-payments.refund-successful');
        return response;
      })
      .catch((err) => {
        this._notificationService.httpError(err);
        throw err;
      });
  }
}
